// Every value carried by cookies with this name, in document.cookie order.
// Duplicate names are a real production state: a stale host-only cookie (e.g.
// jaakko.local) shadows the domain-scoped one (.jaakko.local) that the browser
// actually sends to the API host — so trusting the first match makes every
// mutation fail CSRF double-submit until the shadow cookie is deleted.
function getAllCookieValues(name: string): string[] {
  return document.cookie
    .split(';')
    .map((c) => c.trim().split('='))
    .filter(([cookieName]) => cookieName === name)
    .map(([, value]) => decodeURIComponent(value ?? ''))
    .filter(Boolean);
}

// True for flask-jwt-extended's pre-handler CSRF rejection — it fires before
// any route logic runs, so retrying the request with another candidate token
// is side-effect-free for every HTTP method.
function isCsrfMismatch(status: number, data: any): boolean {
  return status === 401 && typeof data?.msg === 'string' && data.msg.includes('CSRF double submit');
}

export async function apiRequest(path: string, options: { method?: string; headers?: Record<string, string>; body?: any; signal?: AbortSignal } = {}) {
  const baseUrl = process.env.REACT_APP_API_URL || '';
  const isAbsolute = /^https?:\/\//i.test(path);
  let url = path;
  if (!isAbsolute) {
    // Join base URL and path safely (avoid double slashes)
    if (baseUrl) {
      if (path.startsWith('/')) {
        url = `${baseUrl}${path}`;
      } else {
        url = `${baseUrl}/${path}`;
      }
    }
  }
  const method = options.method || 'GET';
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers || {}),
  };

  const csrfCandidates = [
    ...getAllCookieValues('csrf_access_token'),
    ...getAllCookieValues('csrf_refresh_token'),
    ...getAllCookieValues('csrf_token'),
  ].filter((value, index, all) => all.indexOf(value) === index);
  if (csrfCandidates.length > 1) {
    // eslint-disable-next-line no-console
    console.warn(`apiRequest: ${csrfCandidates.length} CSRF cookie candidates found — a stale shadow cookie is likely; clear site cookies to silence this`);
  }

  const body = options.body === undefined
    ? undefined
    : typeof options.body === 'string' ? options.body : JSON.stringify(options.body);

  // One attempt per distinct CSRF candidate: the server rejects a wrong token
  // before any handler runs, so trying the next candidate never repeats work.
  let res: Response;
  let isJson: boolean;
  let data: any;
  const attempts = csrfCandidates.length > 0 ? csrfCandidates : [undefined];
  for (let i = 0; i < attempts.length; i++) {
    const candidate = attempts[i];
    const attemptHeaders = { ...headers };
    if (candidate) attemptHeaders['X-CSRF-TOKEN'] = candidate;
    const fetchOptions: RequestInit = { method, headers: attemptHeaders, credentials: 'include' };
    if (options.signal) fetchOptions.signal = options.signal;
    if (body !== undefined) fetchOptions.body = body;
    res = await fetch(url, fetchOptions);
    const contentType = res.headers.get('content-type') || '';
    isJson = contentType.includes('application/json');
    data = isJson ? await res.json() : await res.text();
    if (!(isCsrfMismatch(res.status, isJson ? data : null) && i < attempts.length - 1)) break;
  }

  if (!res!.ok) {
    const parsed = isJson! ? data : { error: data || 'Request failed' };
    // Attach the HTTP status so callers can branch on it (e.g. 401 → prompt
    // login) without losing the parsed error body. Additive: existing readers
    // of `.error` / `.msg` are unaffected.
    const errBody =
      parsed && typeof parsed === 'object'
        ? Object.assign(parsed, { status: res!.status })
        : { error: parsed, status: res!.status };
    throw errBody;
  }

  return data as any;
}


